<template>
  <Transition name="modal" :duration="300">
    <div
      class="relative z-50"
      role="dialog"
      aria-modal="true"
      v-show="state"
    >
      <div class="fixed inset-0 bg-gray-400 bg-opacity-75 transition-opacity" @click="close" />
      <div class="fixed inset-0 z-10 flex items-center justify-center p-4">
        <div
          class="relative flex w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-xl"
          style="max-height: 80vh"
        >
          <div class="flex items-center justify-between border-b border-greyLight px-4 py-3">
            <div class="truncate pr-3 text-sm font-semibold text-grey">
              Подпись документа
            </div>
            <button
              type="button"
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-greyLight text-grey"
              :disabled="signing"
              @click="close"
            >
              <i class="fas fa-times" />
            </button>
          </div>

          <div class="flex-1 overflow-y-auto p-4" style="min-height: 0">
            <div
              v-if="signing"
              class="flex flex-col items-center justify-center py-10 text-grey"
            >
              <div class="h-8 w-8 animate-spin rounded-full border-b-2 border-[#FFD92E]" />
              <div class="mt-3 text-sm text-grey text-opacity-50">Накладываем подпись...</div>
            </div>

            <template v-else-if="step === 'draw'">
              <div class="mb-3 truncate text-sm text-grey">{{ document?.name }}</div>
              <div
                class="rounded-lg border-2 border-dashed border-greyLight bg-background p-2"
              >
                <canvas ref="signatureCanvas" class="signature-canvas" />
              </div>
              <div
                class="mt-3 flex items-center justify-between text-xs text-grey text-opacity-50"
              >
                <span>{{ hasSignature ? "Подпись готова" : "Нарисуйте подпись" }}</span>
                <button
                  type="button"
                  class="flex items-center space-x-1 text-red"
                  :disabled="!hasSignature"
                  @click="clearSignature"
                >
                  <i class="fas fa-eraser" />
                  <span>Очистить</span>
                </button>
              </div>
            </template>

            <template v-else>
              <div class="mb-3 text-sm text-grey">
                Кликните по документу в нужном месте, чтобы разместить подпись
              </div>

              <div v-if="loading" class="flex flex-col items-center justify-center py-10 text-grey">
                <div class="h-8 w-8 animate-spin rounded-full border-b-2 border-[#FFD92E]" />
                <div class="mt-3 text-sm text-grey text-opacity-50">Загружаем документ...</div>
              </div>

              <div
                v-else-if="error"
                class="flex flex-col items-center justify-center py-10 text-center text-grey"
              >
                <i class="fas fa-file-excel text-3xl text-red" />
                <div class="mt-3 text-sm">Не удалось открыть документ</div>
                <div class="mt-1 text-xs text-grey text-opacity-50">{{ error }}</div>
              </div>

              <div ref="positionContainer" class="relative">
                <canvas
                  ref="pdfCanvas"
                  class="cursor-crosshair block rounded-md bg-white shadow"
                  @click="onCanvasClick"
                />
                <img
                  v-if="placed"
                  :src="signatureDataUrl"
                  class="pointer-events-none absolute"
                  :style="signatureImageStyle"
                />
              </div>
            </template>
          </div>

          <div v-if="!signing" class="border-t border-greyLight px-4 py-3">
            <button
              v-if="step === 'draw'"
              type="button"
              class="flex w-full items-center justify-center rounded-lg py-3 text-sm font-semibold transition"
              :class="
                hasSignature
                  ? 'bg-blue text-white active:scale-95'
                  : 'bg-greyLight text-grey text-opacity-30'
              "
              :disabled="!hasSignature"
              @click="onNext"
            >
              Далее
            </button>

            <div v-else class="flex space-x-3">
              <button
                type="button"
                class="flex items-center justify-center rounded-lg bg-greyLight px-6 py-3 text-sm font-semibold text-grey"
                @click="onBack"
              >
                Назад
              </button>
              <button
                type="button"
                class="flex flex-1 items-center justify-center rounded-lg py-3 text-sm font-semibold transition"
                :class="
                  placed
                    ? 'bg-blue text-white active:scale-95'
                    : 'bg-greyLight text-grey text-opacity-30'
                "
                :disabled="!placed"
                @click="onSign"
              >
                <i class="fas fa-pen mr-2" />
                Подписать
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
import { IDocument } from "@/struct/IDocument/IDocument";
import { ISignaturePosition, UtilDocumentSign } from "@/util/Document/UtilDocumentSign";
import * as pdfjsLib from "pdfjs-dist";
import SignaturePad from "signature_pad";
import { Options, Vue } from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import { markRaw } from "@vue/reactivity";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.BASE_URL}pdf.worker.min.mjs`;

@Options({})
export default class SignaturePadModal extends Vue {
  @Prop() state!: boolean;
  @Prop() document!: IDocument;

  private step: "draw" | "position" = "draw";
  private signing = false;
  private signatureDrawn = false;
  private signatureDataUrl = "";
  private sigAspect = 1;

  private loading = false;
  private error = "";
  private pdfTask: pdfjsLib.PDFDocumentLoadingTask | null = null;
  private scale = 1;
  private pageHeight = 0;
  private canvasWidth = 0;
  private placed: (ISignaturePosition & { px: number; py: number }) | null = null;

  private signaturePad: SignaturePad | null = null;

  get hasSignature(): boolean {
    return this.signatureDrawn;
  }

  get signatureImageStyle(): Record<string, string> {
    if (!this.placed) return {};
    const width = this.canvasWidth * 0.4;
    const height = width / this.sigAspect;
    return {
      left: `${this.placed.px - width / 2}px`,
      top: `${this.placed.py - height / 2}px`,
      width: `${width}px`,
      height: `${height}px`,
    };
  }

  @Watch("state")
  onStateChange(newValue: boolean) {
    if (newValue) {
      document.body.classList.add("overflow-hidden");
      this.step = "draw";
      this.placed = null;
      this.signatureDrawn = false;
      this.signatureDataUrl = "";
      this.error = "";
      this.$nextTick(() => this.initCanvas());
    } else {
      document.body.classList.remove("overflow-hidden");
      this.destroyCanvas();
      this.destroyPdf();
    }
  }

  private get canvas(): HTMLCanvasElement {
    return this.$refs.signatureCanvas as HTMLCanvasElement;
  }

  private get pdfCanvas(): HTMLCanvasElement {
    return this.$refs.pdfCanvas as HTMLCanvasElement;
  }

  private get positionContainer(): HTMLDivElement {
    return this.$refs.positionContainer as HTMLDivElement;
  }

  private initCanvas() {
    if (!this.canvas) return;

    const container = this.canvas.parentElement as HTMLDivElement;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = container?.clientWidth || 320;
    const height = 160;

    this.canvas.width = width * ratio;
    this.canvas.height = height * ratio;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.canvas.getContext("2d")?.setTransform(ratio, 0, 0, ratio, 0, 0);

    this.destroyCanvas();
    this.signaturePad = new SignaturePad(this.canvas, {
      penColor: "#1E1E1E",
      backgroundColor: "rgba(255,255,255,0)",
      dotSize: 1.2,
      minWidth: 1,
      maxWidth: 2.5,
    });
    this.signaturePad.addEventListener("beginStroke", () => {
      this.signatureDrawn = true;
    });
    this.signaturePad.addEventListener("endStroke", () => {
      this.signatureDrawn = !this.signaturePad?.isEmpty();
    });
  }

  private clearSignature() {
    this.signaturePad?.clear();
    this.signatureDrawn = false;
  }

  private onNext() {
    if (!this.hasSignature || !this.signaturePad) return;

    this.signatureDataUrl = this.signaturePad.toDataURL("image/png");
    this.sigAspect = this.canvas.width / this.canvas.height;
    this.step = "position";
    this.placed = null;
    this.$nextTick(() => this.renderPdf());
  }

  private onBack() {
    this.step = "draw";
    this.placed = null;
    this.destroyPdf();
  }

  private async renderPdf() {
    if (!this.document?.url) return;

    this.loading = true;
    this.error = "";

    try {
      this.pdfTask = markRaw(pdfjsLib.getDocument(this.document.url));
      const pdf = await this.pdfTask.promise;
      const page = await pdf.getPage(1);

      const containerWidth = this.positionContainer.clientWidth || 320;
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = containerWidth / baseViewport.width;
      const viewport = page.getViewport({ scale });

      const canvas = this.pdfCanvas;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;

      this.scale = scale;
      this.pageHeight = baseViewport.height;
      this.canvasWidth = canvas.width;

      const context = canvas.getContext("2d");
      if (!context) throw new Error("Canvas context not available");
      await page.render({ canvasContext: context, viewport }).promise;
    } catch (e) {
      this.error = (e as Error)?.message || "Ошибка рендеринга";
    } finally {
      this.loading = false;
    }
  }

  private onCanvasClick(event: MouseEvent) {
    const canvas = this.pdfCanvas;
    const rect = canvas.getBoundingClientRect();
    const px = event.clientX - rect.left;
    const py = event.clientY - rect.top;

    this.placed = {
      x: px / this.scale,
      y: this.pageHeight - py / this.scale,
      px,
      py,
    };
  }

  private async onSign() {
    if (!this.placed) return;

    this.signing = true;
    try {
      const response = await fetch(this.document.url);
      const pdfBytes = await response.arrayBuffer();

      const signedBytes = await UtilDocumentSign.overlaySignature(
        pdfBytes,
        this.signatureDataUrl,
        { x: this.placed.x, y: this.placed.y },
      );

      const blob = new Blob([signedBytes], { type: "application/pdf" });
      const objectUrl = URL.createObjectURL(blob);

      this.$emit("signed", objectUrl);
      this.close();
    } catch (e) {
      console.error("Sign error:", e);
    } finally {
      this.signing = false;
    }
  }

  private destroyCanvas() {
    if (this.signaturePad) {
      this.signaturePad.off();
      this.signaturePad = null;
    }
  }

  private destroyPdf() {
    try {
      this.pdfTask?.destroy();
    } catch (e) {
      console.error(e);
    }
    this.pdfTask = null;
  }

  private close() {
    if (!this.signing) {
      this.$emit("close");
    }
  }
}
</script>

<style scoped>
.signature-canvas {
  touch-action: none;
  display: block;
  width: 100%;
  border-radius: 6px;
}
</style>
